/**
 * ClientRecordTabs — the URL-`?tab=`-driven tab shell for the client record
 * page (PR-C1 §4.3). Modeled on AdminReviewTabs: a stable tab union, a
 * TabVisibility object, an `isHidden` predicate, and a `firstVisibleTab`
 * coercion — so a role URL-hacking `?tab=pre-billing` it can't see degrades
 * to a visible tab, never a 403 shell. The page owns tab STATE
 * (searchParams); this module owns the tab MODEL + the rail render.
 *
 * Per-tab visibility MIRRORS the server gate (single source of truth with
 * the page's coercion):
 *   - Care record → read_clinical_record
 *   - Pre-billing → view_invoice or view_prebilling
 *   - People / Documents → their own gates (open in C1; frame-only)
 *   - Notes → always visible on this (already-gated) surface; the S4
 *     landing (Overview retired, founder-ruled 2026-08-23)
 *
 * The rail shares TAB_RAIL_CLASSES with every other underline tab rail
 * in the app; only the SPICE tab's active border colour (pepper, not
 * forest) is its own.
 */

import { type ReactNode } from 'react';
import type { ClientEngagement } from '@/lib/clientRecord';
import { TAB_RAIL_CLASSES } from '@/components/primitives/tabRail';

// S4 (launch-UX, founder-ruled 2026-08-23): Overview is RETIRED — both
// of its jobs failed the single-author truth (its activity log read the
// author her own words back; its rail duplicated the hero and the Care
// record). The record lands on Notes & activity: opening a client means
// standing at the composer with the feed and queue at hand.
export type ClientRecordTab =
  | 'notes'
  | 'care'
  | 'life-logistics'
  | 'people'
  | 'spice'
  | 'documents'
  | 'pre-billing';

/** The tab SET (membership only — see tabOrderFor for the engagement/
 *  viewer-aware ORDER a client actually renders in). Listed in the
 *  ordinary rail order. */
export const VALID_CLIENT_TABS: ReadonlyArray<ClientRecordTab> = [
  'notes',
  'care',
  'life-logistics',
  'people',
  'spice',
  'documents',
  'pre-billing',
];

/** The ordinary order: Notes leads, then the clinical picture, then
 *  People, SPICE, Documents, Pre-billing. SPICE sits between People and
 *  Documents: on a client on both services the care team reads before
 *  the second service's own tab. */
const DEFAULT_TAB_ORDER: ReadonlyArray<ClientRecordTab> = [
  'notes',
  'care',
  'life-logistics',
  'people',
  'spice',
  'documents',
  'pre-billing',
];

/** SPICE-led order: Notes, then SPICE, People, Documents, Pre-billing —
 *  Care record and Life & logistics fall to the END (still gated
 *  separately, on clinical read: a SPICE-only viewer reads them with no
 *  write door). Used for a SPICE-only client, and for a
 *  SPICE-only viewer on a client on both services. */
const SPICE_LED_TAB_ORDER: ReadonlyArray<ClientRecordTab> = [
  'notes',
  'spice',
  'people',
  'documents',
  'pre-billing',
  'care',
  'life-logistics',
];

/**
 * The tab order for this client + viewer (R5): a SPICE-only client leads
 * with SPICE; a client on both services leads with SPICE too when the
 * VIEWER is SPICE-led (does SPICE, not care management, and holds no
 * whole-practice clinical gate). Care record and Life & logistics show
 * to her read-only; hideCareTab/hideLifeLogisticsTab carry the
 * read gate, and this only reorders what's already visible.
 */
export function tabOrderFor(engagement: ClientEngagement | undefined, spiceLedViewer: boolean): ReadonlyArray<ClientRecordTab> {
  const spiceLed = engagement === 'spice' || (engagement === 'both' && spiceLedViewer);
  return spiceLed ? SPICE_LED_TAB_ORDER : DEFAULT_TAB_ORDER;
}

export const CLIENT_TAB_LABELS: Record<ClientRecordTab, string> = {
  notes: 'Notes & activity',
  care: 'Care record',
  // Care-record restructure: the four non-medical registers (Legal &
  // wishes, Insurance, Housing, Addresses) moved out of the clinical
  // picture into their own tab; same aggregate snapshot, no new fetch.
  'life-logistics': 'Life & logistics',
  // SPICE note extraction (0189): the session list, beside Notes — shown
  // only when the client's engagement involves SPICE.
  spice: 'SPICE',
  people: 'People',
  documents: 'Documents',
  'pre-billing': 'Pre-billing',
};

/**
 * Per-tab visibility — each flag mirrors a server gate. Computed ONCE by the
 * page (from whoami caps) and spread into both the rail AND the page's active-
 * tab coercion (single source of truth, exactly like AdminReviewTabs).
 */
export interface ClientTabVisibility {
  /** Hide "Care record" — mirrors read_clinical_record. */
  hideCareTab?: boolean;
  /** Hide "Life & logistics" — mirrors read_clinical_record (the same
   *  gate as the Care record: the four registers ride the one clinical
   *  aggregate snapshot). */
  hideLifeLogisticsTab?: boolean;
  /** Hide "SPICE" — mirrors read_clinical_record, plus the client's
   *  engagement: a pure care_management client has no SPICE facts to show. */
  hideSpiceTab?: boolean;
  /** Hide "Pre-billing": mirrors view_invoice or view_prebilling. */
  hidePreBillingTab?: boolean;
  /** Hide "People" — mirrors the people gate. */
  hidePeopleTab?: boolean;
  /** Hide "Documents" — mirrors the documents gate. */
  hideDocumentsTab?: boolean;
  /** The client's engagement (0137) — decides tab ORDER (tabOrderFor), not
   *  visibility. Undefined (record not yet loaded) renders the default order. */
  engagement?: ClientEngagement;
  /** True for a viewer who does SPICE, not care management, and holds no
   *  whole-practice clinical gate (the SAME viewer hideCareTab/
   *  hideLifeLogisticsTab already exclude from those two tabs on every
   *  engagement) — leads a 'both' client's tab order with SPICE too. */
  spiceLedViewer?: boolean;
}

function makeIsHidden(v: ClientTabVisibility): (t: ClientRecordTab) => boolean {
  return (t: ClientRecordTab): boolean =>
    (t === 'care' && !!v.hideCareTab) ||
    (t === 'life-logistics' && !!v.hideLifeLogisticsTab) ||
    (t === 'spice' && !!v.hideSpiceTab) ||
    (t === 'pre-billing' && !!v.hidePreBillingTab) ||
    (t === 'people' && !!v.hidePeopleTab) ||
    (t === 'documents' && !!v.hideDocumentsTab);
}

/**
 * Whether a tab is hidden — exported so the page coerces its active tab with
 * the SAME logic as the rail (a role URL-hacking to a hidden tab falls back
 * to a visible tab rather than rendering a tab it cannot see).
 */
export function isClientTabHidden(tab: ClientRecordTab, visibility: ClientTabVisibility): boolean {
  return makeIsHidden(visibility)(tab);
}

/** The first visible tab in THIS visibility's own order (tabOrderFor).
 *  Notes is always visible on this (already-gated) surface, so this is
 *  'notes' for every role — the S4 landing. */
export function firstVisibleClientTab(visibility: ClientTabVisibility): ClientRecordTab {
  const isHidden = makeIsHidden(visibility);
  const order = tabOrderFor(visibility.engagement, !!visibility.spiceLedViewer);
  return order.find((t) => !isHidden(t)) ?? 'notes';
}

/** Coerce a raw ?tab= value to a valid ClientRecordTab (default notes).
 *  A stale ?tab=overview bookmark degrades here — same page, no 403
 *  shell, no redirect ceremony (the S4 ruling). */
export function resolveClientTab(raw: string | null): ClientRecordTab {
  if (VALID_CLIENT_TABS.includes(raw as ClientRecordTab)) return raw as ClientRecordTab;
  return 'notes';
}

export interface ClientRecordTabsProps extends ClientTabVisibility {
  activeTab: ClientRecordTab;
  /** Per-tab count badges (e.g. notes, pre-billing). */
  counts?: Partial<Record<ClientRecordTab, number>>;
  onChange: (next: ClientRecordTab) => void;
}

export function ClientRecordTabs({
  activeTab,
  counts,
  hideCareTab,
  hideLifeLogisticsTab,
  hideSpiceTab,
  hidePreBillingTab,
  hidePeopleTab,
  hideDocumentsTab,
  engagement,
  spiceLedViewer,
  onChange,
}: ClientRecordTabsProps): ReactNode {
  const isHidden = makeIsHidden({
    hideCareTab,
    hideLifeLogisticsTab,
    hideSpiceTab,
    hidePreBillingTab,
    hidePeopleTab,
    hideDocumentsTab,
  });
  const visible = tabOrderFor(engagement, !!spiceLedViewer).filter((t) => !isHidden(t));

  return (
    // S3.4 (launch-UX): the honest rail. The scroller moves to an inner
    // wrapper so the right-edge fade can sit OUTSIDE it (a fade inside
    // the scroller would scroll away with the content) — at 375px about
    // three tabs show, and the fade says so instead of keeping the rest
    // a secret. lg:hidden: by lg the full rail fits and the fade retires.
    <nav aria-label="Record sections" className="relative">
      <div className="overflow-x-auto">
        <div
          role="tablist"
          aria-label="Record sections"
          className={`flex gap-1 min-w-max ${TAB_RAIL_CLASSES.tablist}`}
        >
        {visible.map((tab) => {
          const isActive = tab === activeTab;
          const label = CLIENT_TAB_LABELS[tab];
          const count = counts?.[tab];
          const showCount = typeof count === 'number' && count > 0;
          return (
            <button
              key={tab}
              id={`client-tab-${tab}`}
              type="button"
              role="tab"
              aria-selected={isActive}
              aria-controls="client-tabpanel"
              aria-label={showCount ? `${label}, ${count}` : label}
              onClick={() => onChange(tab)}
              className={
                `${TAB_RAIL_CLASSES.tab} ` +
                (isActive
                  ? // The SPICE accent role: the SPICE tab's own active
                    // marker and fill take pepper; every other tab keeps
                    // the forest underline and fill. The label stays the
                    // same ink as every other active tab.
                    (tab === 'spice'
                      ? `border-pepper ${TAB_RAIL_CLASSES.activeSpice}`
                      : `border-forest-700 ${TAB_RAIL_CLASSES.active}`)
                  : TAB_RAIL_CLASSES.inactive)
              }
            >
              {label}
              {showCount && (
                <span
                  aria-hidden="true"
                  className={
                    'inline-flex items-center justify-center min-w-[1.25rem] h-5 px-1.5 rounded-pill text-xs font-semibold ' +
                    // S3.3: the NOTES count is the confirm queue waiting on
                    // the viewer, gold means exactly that, and only that.
                    // Any other tab's count keeps the neutral pill.
                    (tab === 'notes'
                      ? 'bg-gold-soft text-caution-deep'
                      : 'bg-sage-800 text-cream')
                  }
                >
                  {count}
                </span>
              )}
            </button>
          );
        })}
        </div>
      </div>
      {/* S3.4: the edge fade — outside the scroller, never interactive. */}
      <div
        data-testid="tab-rail-fade"
        aria-hidden="true"
        className="pointer-events-none absolute inset-y-0 right-0 w-10 bg-gradient-to-l from-cream to-transparent lg:hidden"
      />
    </nav>
  );
}
